/**
 * Created by Ning Jiang on 11/11/2019.
 */

import {EventHandler} from "@nolimitcity/slot-game/bin/core/event/EventHandler";
import {GameEvent} from "@nolimitcity/slot-game/bin/core/event/GameEvent";
import {GameConfig} from "@nolimitcity/slot-game/bin/core/gameconfig/GameConfig";
import {GameMode} from "@nolimitcity/slot-game/bin/core/gamemode/GameMode";
import {Reels} from "@nolimitcity/slot-game/bin/core/reel/reelarea/Reels";
import {StateReelSymbol} from "@nolimitcity/slot-game/bin/core/reelsymbol/StateReelSymbol";
import {ScreenEvent} from "@nolimitcity/slot-game/bin/core/screen/event/ScreenEvent";
import {ServerEvent} from "@nolimitcity/slot-game/bin/core/server/event/ServerEvent";
import {SpinEvent} from "@nolimitcity/slot-game/bin/core/spin/event/SpinEvent";
import {WinPresentationController} from "@nolimitcity/slot-game/bin/core/winpresentation/WinPresentationController";
import {Timeline, TimelineLite} from "gsap";
import {DWBackground} from "../../background/DWBackground";
import {DeadwoodGameConfig} from "../../DeadwoodGameConfig";
import {DeadwoodReelView} from "../../reel/DeadwoodReelView";
import {DeadwoodParsedGameData} from "../../server/data/DeadwoodParsedGameData";
import {DeadwoodParsedInitData} from "../../server/data/DeadwoodParsedInitData";
import {IDeadwoodParsedServerData} from "../../server/data/IDeadwoodParsedServerData";
import {DeadwoodReelSymbol} from "../../symbols/DeadwoodReelSymbol";
import {BonusWinPresentationView} from "./BonusWinPresentationView";

export class BonusWinPresentationController extends WinPresentationController {

    private _mode:string;
    private _nextMode:string;
    private _overlayWilds:boolean[][];
    private _bonusTriggered:boolean;
    private _bonusPositions:number[];
    private _reels:string[][];
    private _doReelNudge:boolean[];
    private _doNudge:boolean;
    private _fsSuperModeActive:boolean;

    private _view:BonusWinPresentationView;
    private _mainTimeline:TimelineLite | null;
    private _introTimeline:TimelineLite;
    private _introDisplayedProgress:number;
    private _wildsTimeline:TimelineLite | null;

    constructor(indexes:[number, number]) {
        super(indexes, {name : "BonusWild", skippable : true});
        this._view = new BonusWinPresentationView();
    }

    protected addFeatureEventHandlers():void {
        EventHandler.addEventListener(this, ServerEvent.INIT_DATA_PARSED, (event:GameEvent) => this.onInitDataParsed(event.params[0]));
        EventHandler.addEventListener(this, ScreenEvent.GAME_RESTORE, (event:GameEvent) => this.onGameRestore());
        EventHandler.addEventListener(this, SpinEvent.BEFORE_START, (event:GameEvent) => this.onBeforeSpinStart());
    }

    private onBeforeSpinStart():void {
        if(this._nextMode === GameMode.NORMAL) {
            EventHandler.dispatchEvent(new GameEvent(DWBackground.SHOW_SHOOTOUT_LOGO, false));
        }
    }

    private parsePrivateData(data:IDeadwoodParsedServerData):void {
        this._mode = data.mode;
        this._nextMode = data.nextMode;
        this._overlayWilds = data.overlayWilds!;
        this._bonusTriggered = data.bonusTriggeredThisSpin;
        this._fsSuperModeActive = data.fsSuperModeActive;
        this._reels = data.reels.concat();
    }

    private onInitDataParsed(data:DeadwoodParsedInitData):void {
        this.parsePrivateData(data);
    }

    protected parseFeatureGameData(data:DeadwoodParsedGameData):void {
        this.parsePrivateData(data);
        this._bonusPositions = data.bonusPositions;
        this._doReelNudge = data.doReelNudge;
        this._doNudge = false;
        if(data.doReelNudge) {
            for(let i:number = 1; i < 4; i++) {
                if(this._doReelNudge[i] && (!this._reels[i][1].includes("W") || !this._reels[i][4].includes("W"))) {
                    this._doNudge = true;
                    break;
                }
            }
        }
    }

    // restore low symbol wild in fs before spin starts.
    // There is no restore case in main game, so no need to parse mode.
    private onGameRestore():void {
        if(!this._fsSuperModeActive) {
            return;
        }

        EventHandler.dispatchEvent(new GameEvent(DWBackground.BURN_BONUS_FRAME, true, false));
        EventHandler.dispatchEvent(new GameEvent(DWBackground.SHOW_SHOOTOUT_LOGO, true));
        for(let i:number = 1; i < 4; i++) {
            for(let j:number = 0; j < 4; j++) {
                const symbol = <DeadwoodReelSymbol>Reels.getSymbol(i, j);
                if(symbol && symbol.symName.includes("L")) {
                    symbol.showWild(false);
                }
            }
        }
    }

    protected hasWin():boolean {
        return this._bonusTriggered || this._fsSuperModeActive;
    }

    protected startWinPresentation():void {
        this.createIntroTimeline();
        this.createWildsTimeline();

        const timeline:TimelineLite = new TimelineLite({paused : true});
        // timeline.add(() => EventHandler.dispatchEvent(new GameEvent(SoundEvent.PLAY_SOUND_EFFECT, TombstoneSoundConfig.instance.BONUS_WIN)), 0);
        // if there is no nudge in between the B win attention and bonus intro, skip playing B symbol animations again.
        let playBonusSymbol:boolean = this._doNudge || (this._bonusTriggered && this._fsSuperModeActive);
        if(playBonusSymbol) {
            timeline.add(() => this.getBonusSymbolAnimation(0).play(), 0);
            timeline.add(() => this.getBonusSymbolAnimation(4).play(), 0);
        }

        let timePosition:number = timeline.totalDuration() + (playBonusSymbol ? 0.3 : 0);

        let playIntro:boolean = this._bonusTriggered && this._fsSuperModeActive;
        if(playIntro) {
            timeline.add(() => {
                this._view.setMode(this._mode);
                this._introTimeline.timeScale(1);
                this._introTimeline.restart();
            }, timePosition);
            timePosition += this._introTimeline.totalDuration();
            timeline.add(() => EventHandler.dispatchEvent(new GameEvent(DWBackground.SHOW_SHOOTOUT_LOGO, true)), timePosition);
        } else {
            if(this._bonusTriggered) {
                timeline.add(() => EventHandler.dispatchEvent(new GameEvent(DWBackground.SHOW_SHOOTOUT_LOGO, true)), timePosition);
            }
            timePosition += 0.5;
        }

        timeline.add(() => this._wildsTimeline!.play(), timePosition);
        timePosition += this._wildsTimeline!.totalDuration() + 0.1;
        timeline.add(() => this.finish(), timePosition);
        this._mainTimeline = timeline;
        this._mainTimeline.play();
    }

    private createIntroTimeline():void {
        if(!this._introTimeline) {
            const introTimeline:TimelineLite = new TimelineLite({paused : true});
            // introTimeline.add(() => EventHandler.dispatchEvent(new GameEvent(SoundEvent.PLAY_SOUND_EFFECT, TombstoneSoundConfig.instance.JUSTICE_INTRO)));
            this._view.addIntroToTimeline(introTimeline);
            // introTimeline.add(() => this.playRSSound(), "displayed");
            this._introTimeline = introTimeline;
            this._introDisplayedProgress = this._introTimeline.getLabelTime("displayed") / this._introTimeline.duration();
        }
    }

    private createWildsTimeline():void {
        const wildsTimeline:TimelineLite = new TimelineLite({paused : true});
        let hasSymbol:boolean = false;
        this._overlayWilds.forEach((reelOverlay:boolean[], reelId:number) => {
            // only in middle reels
            if(reelId === 0 || reelId === 4) {
                return;
            }
            //This is after nudge
            if(this._doReelNudge[reelId]) {
                // if it's stacked W
                return;
            }
            reelOverlay.forEach((symbolOverlay:boolean, symId:number) => {
                if(symbolOverlay) {
                    // new random symbol wild.
                    const symbol = <DeadwoodReelSymbol>Reels.getSymbol(reelId, symId);
                    if(symbol) {
                        wildsTimeline.add(symbol.showWild(true), 0);
                        hasSymbol = true;
                    }
                }
            });
        });
        if(hasSymbol) {
            this._view.addWildsEffectsToTimeline(wildsTimeline);
        }

        this._wildsTimeline = wildsTimeline;
    }

    private getBonusSymbolAnimation(reelId:number):Timeline {
        const symbol:StateReelSymbol | null = Reels.getSymbol(reelId, this._bonusPositions[reelId]);
        if(symbol == null) {
            debugger;
            return new TimelineLite();
        }
        const timeline:TimelineLite = new TimelineLite({paused : true});
        timeline.add([
            () => (<DeadwoodReelView>Reels.getReel(reelId)!.view).addSymbolToWinContainer(symbol),
            symbol.changeState({
                state : DeadwoodGameConfig.instance.SYMBOL_STATES.landing,
                replay : true,
                fadeInDuration : 0.1,
                onCompleteCallback : () => {
                    if(this._fsSuperModeActive && this._mode != GameMode.NORMAL) {
                        symbol.changeState({
                            state : DeadwoodGameConfig.instance.SYMBOL_STATES.broken
                        });
                    } else {
                        symbol.changeState({
                            state : GameConfig.instance.SYMBOL_STATES.win
                        });
                    }
                }
            })
        ]);

        return timeline;
    }

    protected stopWinPresentation(isAborted:boolean):void {
        if(this._mainTimeline) {
            if(this._mainTimeline.isActive()) {
                this._mainTimeline.pause();
                this._mainTimeline.kill();
            }
            this._mainTimeline = null;
        }
        if(isAborted) {
            (<DeadwoodReelView>Reels.getReel(0)!.view).addSymbolToWinContainer(Reels.getSymbol(0, this._bonusPositions[0])!);
            (<DeadwoodReelView>Reels.getReel(4)!.view).addSymbolToWinContainer(Reels.getSymbol(4, this._bonusPositions[4])!);

            if(this._bonusTriggered && this._fsSuperModeActive) {
                this._view.setMode(this._mode);
                this.createIntroTimeline();
                if(this._introTimeline.progress() === 1) {
                    this._introTimeline.restart();
                }
                if(this._introTimeline.progress() <= this._introDisplayedProgress) {
                    this._introTimeline.timeScale(3).progress(this._introDisplayedProgress).play();
                } else {
                    this._introTimeline.timeScale(3).play();
                }
            }

            if(this._bonusTriggered) {
                EventHandler.dispatchEvent(new GameEvent(DWBackground.SHOW_SHOOTOUT_LOGO, true));
            }

            if(this._wildsTimeline) {
                if(this._wildsTimeline.progress() === 0) {
                    this._wildsTimeline.play();
                    this._wildsTimeline.progress(1);
                    this._wildsTimeline.kill();
                } else if(this._wildsTimeline.isActive()) {
                    this._wildsTimeline.progress(1);
                    this._wildsTimeline.kill();
                }
            } else {
                this._overlayWilds.forEach((reelOverlay:boolean[], reelId:number) => {
                    // only in middle reels
                    if(reelId === 0 || reelId === 4) {
                        return;
                    }
                    //This is after nudge
                    if(this._doReelNudge[reelId]) {
                        // if it's stacked W
                        return;
                    }
                    reelOverlay.forEach((symbolOverlay:boolean, symId:number) => {
                        if(symbolOverlay) {
                            // new random symbol wild.
                            const symbol = <DeadwoodReelSymbol>Reels.getSymbol(reelId, symId);
                            if(symbol) {
                                symbol.showWild(false);
                            }
                        }
                    });
                });
            }
        }
        this._wildsTimeline = null;
    }
}